import * as THREE from 'three';
import { FLOOR } from './kli.js';
import { C, M, mat, mesh, piece, canvasTexture, roundRect, FONT } from './accessories.js';

/*
 * Props for the KaliAI product site: one per feature of the platform, plus outfits
 * for the industries it serves. Same rules as kliento/accessories.js: primitives in
 * the KLIENTO palette, head-worn pieces on kli.head, floating props on kli.stage.
 */

function rrShape(w, h, r) {
  const s = new THREE.Shape(), x = -w / 2, y = -h / 2;
  s.moveTo(x + r, y); s.lineTo(x + w - r, y); s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r); s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h); s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r); s.quadraticCurveTo(x, y, x + r, y);
  return s;
}
function roundedSlab(w, h, d, r = 0.08) {
  const g = new THREE.ExtrudeGeometry(rrShape(w, h, r), { depth: d, bevelEnabled: true, bevelSize: 0.015, bevelThickness: 0.015, bevelSegments: 2, curveSegments: 8 });
  g.translate(0, 0, -d / 2);
  return g;
}

/* Kali's sparkle ✦ (from the app icon) -------------------------------------- */
function sparkleGeometry(r = 0.3) {
  const s = new THREE.Shape();
  const k = r * 0.16;   // how pinched the waist is
  s.moveTo(0, r);
  s.quadraticCurveTo(k, k, r, 0);
  s.quadraticCurveTo(k, -k, 0, -r);
  s.quadraticCurveTo(-k, -k, -r, 0);
  s.quadraticCurveTo(-k, k, 0, r);
  const g = new THREE.ExtrudeGeometry(s, { depth: 0.06, bevelEnabled: true, bevelSize: 0.02, bevelThickness: 0.03, bevelSegments: 3, curveSegments: 12 });
  g.translate(0, 0, -0.03);
  return g;
}
export function sparkles(kli, spots = [[1.2, 1.0, 0.2, 0.32], [1.55, 0.45, -0.1, 0.18], [-1.25, 0.9, 0.1, 0.2]]) {
  const g = new THREE.Group();
  const stars = spots.map(([x, y, z, r], i) => {
    const m = mesh(sparkleGeometry(r), i % 2 ? M.white : M.blue, [x, y, z]);
    m.userData = { y, phase: i * 2.1 };
    g.add(m);
    return m;
  });
  kli.stage.add(g);
  let spin = 0;
  return piece([g], (dt, t) => {
    spin = Math.max(0, spin - dt * 1.5);
    stars.forEach((s) => {
      const tw = 0.85 + 0.15 * Math.sin(t * 3 + s.userData.phase);
      s.scale.setScalar(tw);
      s.rotation.y = Math.sin(t * 0.8 + s.userData.phase) * 0.5 + spin * Math.PI * 2;
      s.position.y = s.userData.y + Math.sin(t * 1.3 + s.userData.phase) * 0.05;
    });
  }, () => { spin = 1; });
}

/* Desk calendar: appointments + Google Calendar sync ------------------------ */
const MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const SLOTS = ['09:00', '10:30', '13:00', '16:30'];
export function calendar(kli, { x = 1.45, z = 0.35, ry = -0.45 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, FLOOR, z);
  g.rotation.y = ry;
  g.scale.setScalar(1.3);
  const tilt = new THREE.Group();
  tilt.rotation.x = -0.18;
  g.add(tilt);
  let day = new Date().getDate(), month = new Date().getMonth(), slot = 1;
  const page = canvasTexture(320, 340, (c, w, h, d = day, s = slot) => {
    c.fillStyle = '#fff'; roundRect(c, 0, 0, w, h, 26); c.fill();
    c.fillStyle = C.blue; roundRect(c, 0, 0, w, 78, [26, 26, 0, 0]); c.fill();
    c.direction = 'rtl'; c.textAlign = 'center';
    c.fillStyle = '#fff'; c.font = `700 34px ${FONT}`; c.fillText(MONTHS[month], w / 2, 52);
    c.fillStyle = C.ink; c.font = `900 120px ${FONT}`; c.fillText(String(d), w / 2, 205);
    c.fillStyle = '#E0F3FA'; roundRect(c, 40, 236, w - 80, 60, 30); c.fill();
    c.fillStyle = C.blue700; c.font = `600 30px ${FONT}`; c.fillText(`${SLOTS[s]} ✓`, w / 2, 277);
  });
  const front = new THREE.MeshBasicMaterial({ map: page.tex, toneMapped: false, transparent: true });
  const block = new THREE.Group();
  block.position.y = 0.52;
  block.add(mesh(new THREE.BoxGeometry(0.88, 0.94, 0.12), M.white));
  block.add(mesh(new THREE.PlaneGeometry(0.88, 0.94), front, [0, 0, 0.061]));
  for (const s of [-1, 1]) block.add(mesh(new THREE.TorusGeometry(0.07, 0.022, 8, 20), M.ink, [s * 0.22, 0.48, 0], [0, Math.PI / 2, 0]));
  tilt.add(block);
  // the page that flips over the top
  const flipPivot = new THREE.Group();
  flipPivot.position.set(0, 0.99, 0.065);
  const flipMat = new THREE.MeshBasicMaterial({ map: page.tex, toneMapped: false, transparent: true, side: THREE.DoubleSide });
  const flipPage = mesh(new THREE.PlaneGeometry(0.88, 0.94), flipMat, [0, -0.47, 0]);
  flipPivot.add(flipPage);
  flipPivot.visible = false;
  tilt.add(flipPivot);
  kli.stage.add(g);
  document.fonts?.ready.then(() => page.redraw());

  let clock = 0, flip = 0;
  const next = () => {
    if (flip) return;
    flip = 1;
    flipPivot.visible = true;
    flipMat.map = page.tex.clone(); flipMat.map.needsUpdate = true;   // freeze the old page on the flipping sheet
    day = day % 28 + 1; slot = (slot + 1) % SLOTS.length;
    page.redraw();
  };
  return piece([g], (dt, t) => {
    clock += dt;
    if (clock > 3.2) { clock = 0; next(); }
    if (flip) {
      flip = Math.max(0, flip - dt * 1.8);
      flipPivot.rotation.x = (1 - flip) * Math.PI * 0.95;
      if (!flip) { flipPivot.visible = false; flipMat.map.dispose(); }
    }
    g.position.y = FLOOR + Math.abs(Math.sin(t * 1.5)) * 0.03;
  }, () => { clock = 0; next(); });
}

/* Megaphone: broadcasts & campaigns ----------------------------------------- */
export function megaphone(kli, { x = 1.25, y = 0.05, z = 0.4 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.rotation.z = 0.25;
  const horn = new THREE.Group();
  horn.rotation.z = -Math.PI / 2;   // the wide end points away from Kli
  horn.add(mesh(new THREE.CylinderGeometry(0.11, 0.36, 0.7, 32, 1, true), mat(C.blue, { side: THREE.DoubleSide })));
  horn.add(mesh(new THREE.TorusGeometry(0.36, 0.035, 10, 40), M.white, [0, -0.35, 0], [Math.PI / 2, 0, 0]));
  horn.add(mesh(new THREE.CylinderGeometry(0.12, 0.12, 0.16, 24), M.ink, [0, 0.42, 0]));
  horn.add(mesh(new THREE.BoxGeometry(0.09, 0.3, 0.1), M.ink, [0.18, 0.2, 0], [0, 0, 0.5]));
  g.add(horn);
  // little chat bubbles flying out
  const bubbleGeo = new THREE.SphereGeometry(0.16, 18, 12);
  const out = [0, 1, 2, 3].map((i) => {
    const b = new THREE.Group();
    const skin = mesh(bubbleGeo, new THREE.MeshStandardMaterial({ color: '#fff', roughness: 0.5, transparent: true }));
    skin.scale.set(1.25, 0.9, 0.5);
    b.add(skin);
    b.add(mesh(new THREE.SphereGeometry(0.035, 8, 6), new THREE.MeshBasicMaterial({ color: C.blue, toneMapped: false }), [0, 0, 0.08]));
    b.userData.o = i / 4;
    g.add(b);
    return b;
  });
  kli.stage.add(g);
  let boost = 0;
  return piece([g], (dt, t) => {
    boost = Math.max(0, boost - dt);
    out.forEach((b) => {
      const p = (t * (0.45 + boost) + b.userData.o) % 1;
      b.position.set(0.45 + p * 1.1, Math.sin(p * 6 + b.userData.o * 9) * 0.12 * p + (b.userData.o - 0.4) * p * 0.8, 0);
      b.scale.setScalar(0.5 + p * 0.8);
      b.children[0].material.opacity = 1 - p * p;
    });
    g.rotation.z = 0.25 + Math.sin(t * 2) * 0.05;
  }, () => { boost = 2; });
}

/* Flow graph: the visual Flow Builder --------------------------------------- */
export function flowGraph(kli, { x = 1.7, y = 0.2, z = -0.1 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.scale.setScalar(1.45);
  g.rotation.y = -0.35;
  const nodeGeo = roundedSlab(0.42, 0.22, 0.08, 0.06);
  const nodes = [
    [0, 0.55, M.blue], [-0.32, 0.05, M.white], [0.32, 0.05, M.white], [0, -0.45, M.ink],
  ].map(([nx, ny, m]) => { const n = mesh(nodeGeo, m, [nx, ny, 0]); g.add(n); return n; });
  const edges = [[0, 1], [0, 2], [1, 3], [2, 3]].map(([a, b]) => {
    const p0 = nodes[a].position.clone().add(new THREE.Vector3(0, -0.12, 0));
    const p1 = nodes[b].position.clone().add(new THREE.Vector3(0, 0.12, 0));
    const curve = new THREE.CubicBezierCurve3(p0, p0.clone().setY(p0.y - 0.2), p1.clone().setY(p1.y + 0.2), p1);
    g.add(mesh(new THREE.TubeGeometry(curve, 20, 0.014, 6), M.ink));
    return curve;
  });
  const runner = mesh(new THREE.SphereGeometry(0.045, 12, 8), new THREE.MeshBasicMaterial({ color: C.blue300, toneMapped: false }));
  g.add(runner);
  kli.stage.add(g);
  let pulse = 0;
  return piece([g], (dt, t) => {
    // a message runs down the graph: start -> one branch -> end
    const branch = Math.floor(t / 2.4) % 2;
    const p = (t % 2.4) / 2.4;
    const curve = p < 0.5 ? edges[branch] : edges[2 + branch];
    curve.getPoint((p % 0.5) * 2, runner.position);
    runner.position.z = 0.07;
    pulse = Math.max(0, pulse - dt * 2);
    nodes.forEach((n, i) => { n.scale.setScalar(1 + (i === 0 ? pulse * 0.25 : 0)); });
    g.position.y = y + Math.sin(t * 1.2) * 0.05;
  }, () => { pulse = 1; });
}

/* Graduation cap: the knowledge base / Kali learns your business ------------- */
export function gradCap(kli) {
  const g = new THREE.Group();
  g.position.set(0, 0.74, -0.05);
  g.rotation.x = -0.12;
  g.add(mesh(new THREE.CylinderGeometry(0.58, 0.62, 0.3, 32), M.ink, [0, 0.12, 0]));
  const board = mesh(new THREE.BoxGeometry(1.35, 0.06, 1.35), M.ink, [0, 0.3, 0], [0, Math.PI / 4, 0]);
  g.add(board);
  g.add(mesh(new THREE.CylinderGeometry(0.06, 0.06, 0.04, 12), M.blue, [0, 0.35, 0]));
  const tassel = new THREE.Group();
  tassel.position.set(0, 0.35, 0);
  const cord = mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.75, 6), M.blue, [0.45, 0, 0], [0, 0, Math.PI / 2]);
  tassel.add(cord);
  const drop = new THREE.Group();
  drop.position.set(0.88, 0, 0);
  drop.add(mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.32, 6), M.blue, [0, -0.16, 0]));
  drop.add(mesh(new THREE.ConeGeometry(0.07, 0.2, 12), M.blue, [0, -0.38, 0]));
  tassel.add(drop);
  tassel.rotation.y = -0.6;
  g.add(tassel);
  kli.head.add(g);
  let swing = 0;
  return piece([g], (dt, t) => {
    swing = Math.max(0, swing - dt * 0.8);
    drop.rotation.z = Math.sin(t * 2.5) * 0.12 + Math.sin(t * 9) * 0.5 * swing - kli.body.rotation.y * 0.4;
    drop.rotation.x = -kli.body.rotation.x * 0.6;
  }, () => { swing = 1; });
}
export function books(kli, { x = 1.3, z = 0.5 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, FLOOR, z);
  g.rotation.y = -0.4;
  const cols = [M.blue, M.white, M.ink, M.blueDeep];
  let y = 0;
  cols.forEach((m, i) => {
    const h = 0.13 + (i % 2) * 0.03;
    const b = mesh(new THREE.BoxGeometry(0.75 - i * 0.05, h, 0.52 - i * 0.03), m, [0, y + h / 2, 0], [0, (i - 1.5) * 0.12, 0]);
    g.add(b);
    y += h;
  });
  // pages edge on the top book
  const top = mesh(sparkleGeometry(0.16), M.blue, [0, y + 0.25, 0]);
  g.add(top);
  kli.stage.add(g);
  return piece([g], (dt, t) => { top.rotation.y = t * 1.2; top.position.y = y + 0.25 + Math.sin(t * 2) * 0.05; });
}

/* Bar chart: reports & dashboard -------------------------------------------- */
export function chart(kli, { x = 1.45, z = 0.25 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, FLOOR, z);
  g.rotation.y = -0.45;
  g.add(mesh(new THREE.BoxGeometry(1.15, 0.05, 0.42), M.white, [0, 0.025, 0]));
  const shades = [M.blueDeep, M.blue, M.blue, M.blue];
  const heights = [0.35, 0.55, 0.75, 1.05];
  const bars = heights.map((h, i) => {
    const b = mesh(new THREE.BoxGeometry(0.2, 1, 0.2), shades[i], [-0.42 + i * 0.28, 0.05, 0]);
    b.userData.h = h;
    g.add(b);
    return b;
  });
  // trend arrow over the bars
  const pts = heights.map((h, i) => new THREE.Vector3(-0.42 + i * 0.28, h + 0.2, 0.13));
  const curve = new THREE.CatmullRomCurve3(pts);
  const line = mesh(new THREE.TubeGeometry(curve, 30, 0.022, 6), M.ink);
  g.add(line);
  const head = mesh(new THREE.ConeGeometry(0.06, 0.15, 12), M.ink, [pts[3].x + 0.04, pts[3].y + 0.07, 0.13], [0, 0, -0.55]);
  g.add(head);
  kli.stage.add(g);
  let grow = 1;
  return piece([g], (dt, t) => {
    grow = Math.min(1, grow + dt * 0.8);
    bars.forEach((b, i) => {
      const h = b.userData.h * Math.min(1, Math.max(0, grow * 1.6 - i * 0.2)) * (1 + 0.06 * Math.sin(t * 2.4 - i));
      b.scale.y = Math.max(0.001, h);
      b.position.y = 0.05 + h / 2;
    });
    const vis = grow > 0.95;
    line.visible = head.visible = vis;
  }, () => { grow = 0; });
}

/* Phone with a QR code: connect your WhatsApp ------------------------------- */
export function qrPhone(kli, { x = 1.5, y = 0.1, z = 0.25, ry = -0.38 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, y, z);
  g.rotation.set(-0.05, ry, 0.06);
  g.add(mesh(roundedSlab(0.8, 1.56, 0.06, 0.12), M.ink));
  // a deterministic fake QR (never scans to anything)
  const cells = 21, rnd = (i) => (Math.sin(i * 12.9898) * 43758.5453) % 1;
  let connected = false, scan = 0;
  const screen = canvasTexture(360, 720, (c, w, h) => {
    c.fillStyle = '#F7FAFB'; roundRect(c, 0, 0, w, h, 40); c.fill();
    c.direction = 'rtl'; c.textAlign = 'center';
    c.fillStyle = C.ink; c.font = `700 30px ${FONT}`; c.fillText(connected ? 'הוואטסאפ מחובר!' : 'סרקו לחיבור', w / 2, 110);
    const size = 250, x0 = (w - size) / 2, y0 = 170, cs = size / cells;
    c.fillStyle = '#fff'; roundRect(c, x0 - 18, y0 - 18, size + 36, size + 36, 20); c.fill();
    if (connected) {
      c.beginPath(); c.arc(w / 2, y0 + size / 2, 90, 0, Math.PI * 2); c.fillStyle = C.blue; c.fill();
      c.strokeStyle = '#fff'; c.lineWidth = 18; c.lineCap = 'round'; c.lineJoin = 'round';
      c.beginPath(); c.moveTo(w / 2 - 40, y0 + size / 2); c.lineTo(w / 2 - 8, y0 + size / 2 + 32); c.lineTo(w / 2 + 44, y0 + size / 2 - 30); c.stroke();
    } else {
      c.fillStyle = C.ink;
      for (let i = 0; i < cells; i++) for (let j = 0; j < cells; j++) {
        const finder = (a, b) => a < 7 && b < 7;
        const inF = finder(i, j) || finder(cells - 1 - i, j) || finder(i, cells - 1 - j);
        if (inF) continue;
        if (Math.abs(rnd(i * 31 + j)) > 0.5) c.fillRect(x0 + i * cs, y0 + j * cs, cs, cs);
      }
      for (const [fi, fj] of [[0, 0], [cells - 7, 0], [0, cells - 7]]) {
        c.fillStyle = C.ink; c.fillRect(x0 + fi * cs, y0 + fj * cs, 7 * cs, 7 * cs);
        c.fillStyle = '#fff'; c.fillRect(x0 + (fi + 1) * cs, y0 + (fj + 1) * cs, 5 * cs, 5 * cs);
        c.fillStyle = C.blue; c.fillRect(x0 + (fi + 2) * cs, y0 + (fj + 2) * cs, 3 * cs, 3 * cs);
      }
      c.fillStyle = 'rgba(45,172,223,.85)'; c.fillRect(x0 - 10, y0 + scan * size, size + 20, 5);
    }
    c.fillStyle = '#5C6B75'; c.font = `400 22px ${FONT}`;
    c.fillText(connected ? 'קלי מתחיל לענות ללקוחות' : 'WhatsApp ← מכשירים מקושרים', w / 2, y0 + size + 80);
  });
  g.add(mesh(new THREE.PlaneGeometry(0.72, 1.44), new THREE.MeshBasicMaterial({ map: screen.tex, toneMapped: false, transparent: true }), [0, 0, 0.056]));
  kli.stage.add(g);
  document.fonts?.ready.then(() => screen.redraw());
  let clock = 0;
  return piece([g], (dt, t) => {
    g.position.y = y + Math.sin(t * 1.5) * 0.05;
    clock += dt;
    if (!connected) { scan = (clock % 1.6) / 1.6; screen.redraw(); }
    if (clock > 4.8) { connected = !connected; clock = 0; screen.redraw(); if (connected) kli.jump(4); }
  }, () => { connected = true; clock = 0; screen.redraw(); kli.jump(4); });
}

/* Industry outfits ----------------------------------------------------------- */
export function sweatband(kli) {
  const g = new THREE.Group();
  g.position.y = 0.42;
  g.rotation.x = -0.08;
  g.add(mesh(new THREE.TorusGeometry(0.98, 0.1, 14, 64), M.white, null, [Math.PI / 2, 0, 0]));
  for (const dy of [-0.05, 0.05]) g.add(mesh(new THREE.TorusGeometry(1.0, 0.025, 8, 64), M.blue, [0, dy, 0], [Math.PI / 2, 0, 0]));
  kli.head.add(g);
  return piece([g]);
}
export function headMirror(kli) {
  const g = new THREE.Group();
  g.position.y = 0.45;
  g.rotation.x = -0.15;
  g.add(mesh(new THREE.TorusGeometry(0.99, 0.035, 8, 64), M.ink, null, [Math.PI / 2, 0, 0]));
  const disc = new THREE.Group();
  disc.position.set(0, 0.16, 0.98);
  disc.rotation.x = -0.35;
  disc.add(mesh(new THREE.CylinderGeometry(0.22, 0.22, 0.04, 32), M.metal, null, [Math.PI / 2, 0, 0]));
  disc.add(mesh(new THREE.TorusGeometry(0.22, 0.03, 8, 32), M.blue));
  disc.add(mesh(new THREE.CircleGeometry(0.05, 16), new THREE.MeshBasicMaterial({ color: C.ink }), [0, 0, 0.022]));
  g.add(disc);
  kli.head.add(g);
  return piece([g]);
}
export function headBow(kli) {
  const g = new THREE.Group();
  g.position.set(0.4, 0.9, 0.45);
  g.rotation.set(-0.45, 0.35, -0.4);
  g.scale.setScalar(1.3);
  for (const s of [-1, 1]) {
    const wing = mesh(new THREE.ConeGeometry(0.2, 0.36, 24), M.blue, [s * 0.18, 0, 0], [0, 0, s * Math.PI / 2]);
    wing.scale.z = 0.5;
    g.add(wing);
  }
  g.add(mesh(new THREE.SphereGeometry(0.09, 16, 12), M.white));
  kli.head.add(g);
  return piece([g]);
}
export function shoppingBag(kli, { x = 1.3, z = 0.45 } = {}) {
  const g = new THREE.Group();
  g.position.set(x, FLOOR, z);
  g.rotation.y = -0.4;
  g.add(mesh(new THREE.BoxGeometry(0.62, 0.68, 0.32), M.blue, [0, 0.34, 0]));
  g.add(mesh(new THREE.TorusGeometry(0.16, 0.025, 8, 24, Math.PI), M.ink, [0, 0.68, 0.09]));
  g.add(mesh(new THREE.TorusGeometry(0.16, 0.025, 8, 24, Math.PI), M.ink, [0, 0.68, -0.09]));
  g.add(mesh(new THREE.TorusGeometry(0.1, 0.035, 10, 24), M.white, [0, 0.36, 0.165]));   // the O mark
  kli.stage.add(g);
  let hop = 0;
  return piece([g], (dt) => {
    hop = Math.max(0, hop - dt * 2.5);
    g.position.y = FLOOR + Math.sin(hop * Math.PI) * 0.25;
  }, () => { hop = 1; });
}
